// --- React
import React, { useState, useRef, useEffect } from "react";
// --- UI Components
import Input from "@dtci/spellbook/Input";

/**
 * EditableTextRenderer Component
 *
 * Cell renderer for AG Grid that shows a text input when in edit mode,
 * or plain text when not editing.
 *
 * @param {Function} getEditingRows - Function that returns current editing rows state
 * @param {Function} onValueChange - Callback when value changes (data, fieldName, newValue)
 * @param {string} fieldName - The field name being edited
 * @param {Object} options - Optional config: { maxLength, pattern }
 * @returns {Function} The cell renderer component
 */
export const EditableTextRenderer = (
  getEditingRows,
  onValueChange,
  fieldName,
  options = {},
) => {
  const { maxLength, pattern } = options;

  const Renderer = ({ value, data }) => {
    const editingRows = getEditingRows ? getEditingRows() : {};
    const isEditing = editingRows[data?.id];

    // Use local state for smooth typing - initialized once when entering edit mode
    const [localValue, setLocalValue] = useState(value || "");
    const initializedRef = useRef(false);

    // Reset local state when entering edit mode
    useEffect(() => {
      if (isEditing && !initializedRef.current) {
        setLocalValue(value || "");
        initializedRef.current = true;
      } else if (!isEditing) {
        initializedRef.current = false;
      }
    }, [isEditing, value]);

    if (!isEditing) {
      return <span>{value || ""}</span>;
    }

    const handleChange = (e) => {
      let newValue = e.target.value;

      // Apply maxLength if specified
      if (maxLength && newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength);
      }

      // Apply pattern validation if specified (e.g., no special chars)
      if (pattern && !pattern.test(newValue) && newValue !== "") {
        return; // Don't update if pattern doesn't match
      }

      setLocalValue(newValue);
    };

    // Only sync to parent on blur to prevent grid scroll issues
    const handleBlur = () => {
      if (onValueChange && localValue !== (value || "")) {
        onValueChange(data, fieldName, localValue);
      }
    };

    return (
      <Input
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        width="auto"
        style={{ minWidth: 100 }}
      />
    );
  };

  return Renderer;
};
